import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactElement, ReactNode } from "react";

import {
  apiClient,
  clearAdminJwt as clearAdminJwtStorage,
  getAdminJwt,
} from "../api/client";
import type { SessionInfo } from "../api/types";

interface AuthContextValue {
  session: SessionInfo | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  setAdminJwt: (jwt: string) => void;
  clearAdminJwt: () => void;
  hasAdminJwt: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  // Pre-validate JWT при mount — getAdminJwt() авто-очищает expired token
  const [hasAdminJwt, setHasAdminJwt] = useState<boolean>(() => !!getAdminJwt());

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<SessionInfo>("/auth/session");
      setSession(data);
    } catch {
      setSession({ authenticated: false });
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch {
      // Ignore — session probably already invalid
    }
    setSession({ authenticated: false });
  }, []);

  const setAdminJwt = useCallback((jwt: string) => {
    localStorage.setItem("cp_admin_jwt", jwt);
    sessionStorage.removeItem("cp_jwt_expired_notified");
    setHasAdminJwt(true);
  }, []);

  const clearAdminJwt = useCallback(() => {
    clearAdminJwtStorage();
    setHasAdminJwt(false);
  }, []);

  // Re-check JWT каждые 60 секунд + при window focus — если expired, обновить state
  useEffect(() => {
    const check = () => {
      const valid = !!getAdminJwt();
      setHasAdminJwt((prev) => (prev !== valid ? valid : prev));
    };
    const interval = setInterval(check, 60_000);
    window.addEventListener("focus", check);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", check);
    };
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider
      value={{ session, loading, refresh, logout, setAdminJwt, clearAdminJwt, hasAdminJwt }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
